import { AlertTriangle, Copy, Flag, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  buildFlagOverview,
  matchesSearch,
  useRuntimeFlags,
  type RuntimeFlagsPayload,
  type RuntimeEnvironmentKey,
  type RuntimeService,
  type RuntimeVarClassFilter,
} from "./runtimeFlags";
import { ContinuityControlCard } from "./ContinuityControlCard";
import { H3ControlCard } from "./H3ControlCard";
import { EnvironmentComparisonTable } from "./EnvironmentComparisonTable";
import { RuntimeEnvironmentStateNotice } from "./RuntimeEnvironmentStateNotice";
import { RuntimeVariableInfo } from "./RuntimeVariableInfo";
import { RuntimeVariableValue } from "./RuntimeVariableValue";

const NONE = "—";

type ViewMode = "compare" | RuntimeEnvironmentKey;

const CLASS_FILTERS: Array<{ id: RuntimeVarClassFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "flag", label: "Flags" },
  { id: "plain", label: "Config" },
  { id: "secret", label: "Secrets" },
];

function environmentBadgeLabel(payload: RuntimeFlagsPayload): string {
  const label = payload.environment_label || payload.environment;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function ServiceStatusBadges({ service }: { service: RuntimeService }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant="outline" className="font-mono text-xs font-normal">
        {service.kind}
      </Badge>
      {service.status === "deploying" ? (
        <Badge
          variant="secondary"
          title="An ECS rollout is in flight; values are from the PRIMARY deployment."
        >
          deploying
        </Badge>
      ) : null}
      {service.status === "absent" ? (
        <Badge variant="outline" className="text-muted-foreground">
          not deployed in this environment
        </Badge>
      ) : null}
      {service.status === "error" ? (
        <Badge variant="destructive" title={service.error}>
          read error
        </Badge>
      ) : null}
      {service.revision_drift ? (
        <Badge
          variant="destructive"
          title={`The state machine pins ${service.revision} but the latest active definition is ${service.latest_revision}. New deploys are not being picked up.`}
        >
          <AlertTriangle className="mr-1 size-3" />
          revision drift
        </Badge>
      ) : null}
    </div>
  );
}

function CopyNameButton({ name }: { name: string }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-6 text-muted-foreground"
      title={`Copy ${name}`}
      aria-label={`Copy ${name}`}
      onClick={() => {
        void navigator.clipboard?.writeText(name);
      }}
    >
      <Copy className="size-3" />
    </Button>
  );
}

function ServiceCard({
  service,
  search,
  classFilter,
}: {
  service: RuntimeService;
  search: string;
  classFilter: RuntimeVarClassFilter;
}) {
  const vars = service.vars.filter(
    (entry) =>
      (classFilter === "all" || entry.class === classFilter) &&
      matchesSearch(entry, search),
  );
  const filtered = service.vars.length - vars.length;
  return (
    <Card className={service.status === "absent" ? "opacity-60" : undefined}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            {service.label}
            <span className="font-mono text-xs font-normal text-muted-foreground">
              {service.resource}
            </span>
          </CardTitle>
          <ServiceStatusBadges service={service} />
        </div>
        {service.revision ? (
          <p className="text-xs text-muted-foreground">
            {service.kind === "batch"
              ? `Job definition ${service.revision}${
                  service.revision_pinned_by_state_machine
                    ? " (pinned by the state machine)"
                    : ""
                }`
              : `Revision ${service.revision}`}
          </p>
        ) : null}
      </CardHeader>
      <CardContent>
        {vars.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {service.status === "absent"
              ? "This service does not exist in this environment."
              : filtered > 0
                ? "No variables match the current filter."
                : "No environment variables."}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Value</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {vars.map((entry) => (
                <TableRow key={`${entry.container ?? ""}:${entry.name}`}>
                  <TableCell className="align-top font-mono text-xs">
                    <div className="flex items-start gap-1">
                      <span>
                        {entry.name}
                        {entry.container ? (
                          <span className="ml-1 text-muted-foreground">
                            ({entry.container})
                          </span>
                        ) : null}
                      </span>
                      <RuntimeVariableInfo name={entry.name} />
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <RuntimeVariableValue entry={entry} />
                  </TableCell>
                  <TableCell className="align-top">
                    <CopyNameButton name={entry.name} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function FlagsOverview({
  payload,
  search,
}: {
  payload: RuntimeFlagsPayload;
  search: string;
}) {
  const services = payload.services.filter((service) =>
    service.vars.some((entry) => entry.class === "flag"),
  );
  const rows = buildFlagOverview(payload.services).filter((row) =>
    row.name.toLowerCase().includes(search.trim().toLowerCase()),
  );
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Flags overview</CardTitle>
        <p className="text-xs text-muted-foreground">
          Every boolean flag across services. Highlighted rows disagree between
          services — the drift this tab exists to expose.
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Flag</TableHead>
              {services.map((service) => (
                <TableHead key={service.id} className="text-center">
                  {service.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.name}
                className={row.mismatch ? "bg-amber-500/10" : undefined}
                data-mismatch={row.mismatch ? "true" : undefined}
              >
                <TableCell className="font-mono text-xs">
                  <div className="flex items-center gap-1">
                    <span>{row.name}</span>
                    {row.mismatch ? (
                      <AlertTriangle className="size-3 text-amber-600" />
                    ) : null}
                    <RuntimeVariableInfo name={row.name} />
                  </div>
                </TableCell>
                {services.map((service) => {
                  const state = row.perService[service.id];
                  return (
                    <TableCell key={service.id} className="text-center">
                      {state === undefined ? (
                        <span className="text-muted-foreground">{NONE}</span>
                      ) : (
                        <Badge
                          variant={state ? "default" : "outline"}
                          className={state ? "" : "text-muted-foreground"}
                        >
                          {state ? "on" : "off"}
                        </Badge>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export const FeatureFlagsPage = () => {
  const stgRuntime = useRuntimeFlags("stg");
  const stagingRuntime = useRuntimeFlags("staging");
  const [view, setView] = useState<ViewMode>("compare");
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState<RuntimeVarClassFilter>("all");

  const stgPayload =
    stgRuntime.state?.status === "ok" ? stgRuntime.state.payload : null;
  const stagingPayload =
    stagingRuntime.state?.status === "ok" ? stagingRuntime.state.payload : null;
  const selectedRuntime = view === "staging" ? stagingRuntime : stgRuntime;
  const payload = view === "staging" ? stagingPayload : stgPayload;
  const hasRenderableData =
    view === "compare"
      ? Boolean(stgPayload && stagingPayload)
      : Boolean(payload);
  const isLoading =
    view === "compare"
      ? stgRuntime.isLoading || stagingRuntime.isLoading
      : selectedRuntime.isLoading;
  const isRefreshing =
    view === "compare"
      ? stgRuntime.isRefreshing || stagingRuntime.isRefreshing
      : selectedRuntime.isRefreshing;

  const refresh = async () => {
    if (view === "compare") {
      await Promise.all([stgRuntime.refresh(), stagingRuntime.refresh()]);
      return;
    }
    await selectedRuntime.refresh();
  };

  return (
    <div className="w-full min-w-0 space-y-4 py-4">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Flag className="size-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Feature Flags</h1>
          {view === "compare" ? (
            <>
              <Badge variant="destructive">Production · aws: stg</Badge>
              <Badge variant="secondary">
                Engineering staging · aws: staging
              </Badge>
              {stgPayload && stagingPayload ? (
                <span className="text-xs text-muted-foreground">
                  production as of{" "}
                  {new Date(stgPayload.generated_at).toLocaleTimeString()} ·
                  engineering as of{" "}
                  {new Date(stagingPayload.generated_at).toLocaleTimeString()}
                </span>
              ) : null}
            </>
          ) : payload ? (
            <>
              <Badge
                variant={
                  payload.environment_label === "production"
                    ? "destructive"
                    : "secondary"
                }
              >
                {environmentBadgeLabel(payload)}
              </Badge>
              <span className="text-xs text-muted-foreground">
                aws: {payload.environment} · as of{" "}
                {new Date(payload.generated_at).toLocaleTimeString()} (cached ≤
                {payload.cache.ttl_seconds}s)
              </span>
            </>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refresh()}
            disabled={isLoading || isRefreshing}
            title="Re-read the live configuration"
          >
            <RefreshCw
              className={`mr-1 size-3.5 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={view} onValueChange={(value) => setView(value as ViewMode)}>
        <TabsList aria-label="Runtime environment view">
          <TabsTrigger value="compare">Compare</TabsTrigger>
          <TabsTrigger value="stg">Production (`stg`)</TabsTrigger>
          <TabsTrigger value="staging">
            Engineering staging (`staging`)
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "compare" ? (
        <>
          <RuntimeEnvironmentStateNotice
            environment="stg"
            state={stgRuntime.state}
            isLoading={stgRuntime.isLoading}
          />
          <RuntimeEnvironmentStateNotice
            environment="staging"
            state={stagingRuntime.state}
            isLoading={stagingRuntime.isLoading}
          />
        </>
      ) : (
        <RuntimeEnvironmentStateNotice
          environment={view}
          state={selectedRuntime.state}
          isLoading={selectedRuntime.isLoading}
        />
      )}

      {view === "stg" && payload ? <ContinuityControlCard /> : null}
      {view !== "compare" ? <H3ControlCard environment={view} /> : null}

      {hasRenderableData ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search variables…"
              aria-label="Search runtime variables"
              className="max-w-xs"
            />
            <div className="flex items-center gap-1">
              {CLASS_FILTERS.map((filter) => (
                <Button
                  key={filter.id}
                  variant={classFilter === filter.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setClassFilter(filter.id)}
                  aria-pressed={classFilter === filter.id}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>

          {view === "compare" && stgPayload && stagingPayload ? (
            <EnvironmentComparisonTable
              stg={stgPayload}
              staging={stagingPayload}
              search={search}
              classFilter={classFilter}
            />
          ) : payload ? (
            <>
              {classFilter === "all" || classFilter === "flag" ? (
                <FlagsOverview payload={payload} search={search} />
              ) : null}

              {payload.services.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  search={search}
                  classFilter={classFilter}
                />
              ))}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

FeatureFlagsPage.path = "/feature-flags";
